import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/layout/ScrollToTop";
import motion from "@/lib/lazy-motion-tags";
import { Helmet } from "react-helmet";
import IconLoader from "@/components/IconLoader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const Projects = () => {
  const projects = [
    {
      name: "فيلا سكنية فاخرة - الرياض",
      type: "سكني",
      location: "الرياض",
      date: "2024",
      doors: 35,
      category: "أبواب ديكورية فاخرة",
      description: "تركيب أبواب ديكورية فاخرة مع تصاميم CNC محفورة للمداخل الرئيسية وأبواب داخلية كلاسيك لباقي الغرف",
    },
    {
      name: "مجمع سكني - جدة",
      type: "تجاري",
      location: "جدة",
      date: "2024",
      doors: 120,
      category: "أبواب داخلية + حمامات",
      description: "مشروع متكامل لتوريد وتركيب أبواب داخلية وأبواب حمامات مقاومة للماء لمجمع سكني يضم 40 شقة",
    },
    {
      name: "فندق خمس نجوم - الدمام",
      type: "فندقي",
      location: "الدمام",
      date: "2023",
      doors: 250,
      category: "أبواب CNC محفورة",
      description: "تصنيع وتركيب أبواب فاخرة بتقنية CNC للوبيات والأجنحة الملكية وأبواب الغرف القياسية",
    },
    {
      name: "قصر ملكي - الرياض",
      type: "قصور",
      location: "الرياض",
      date: "2023",
      doors: 80,
      category: "أبواب CNC + ديكورية",
      description: "مشروع حصري لتوريد أبواب فاخرة بتصاميم مخصصة وتفاصيل دقيقة تعكس الفخامة والأصالة",
    },
    {
      name: "مشروع إسكان - الخبر",
      type: "سكني",
      location: "الخبر",
      date: "2023",
      doors: 180,
      category: "أبواب داخلية",
      description: "توريد وتركيب أبواب داخلية كلاسيك لمشروع إسكان يضم 60 وحدة سكنية",
    },
    {
      name: "مركز تجاري - مكة",
      type: "تجاري",
      location: "مكة",
      date: "2022",
      doors: 95,
      category: "أبواب بدون حلق",
      description: "تركيب أبواب عصرية بدون حلق للمحلات التجارية والمكاتب الإدارية",
    },
  ];

  const stats = [
    { iconName: "Building2", number: "+500", label: "مشروع منجز" },
    { iconName: "Home", number: "+2000", label: "باب مركب" },
    { iconName: "MapPin", number: "+15", label: "مدينة" },
    { iconName: "Calendar", number: "5", label: "سنوات خبرة" },
  ];

  return (
    <>
      <ScrollToTop />
      <Helmet>
        <title>مشاريعنا وسجل النجاح - New Business | +500 مشروع منجز</title>
        <meta name="description" content="تعرف على مشاريعنا المنجزة في جميع أنحاء المملكة. خبرة موثوقة في تركيب أبواب WPC للمشاريع السكنية والتجارية" />
      </Helmet>
      
      <div className="min-h-screen">
        <Navbar />
        
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-background via-muted/30 to-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">مشاريعنا</span> وسجل النجاح
              </h1>
              <p className="text-xl text-muted-foreground">
                فخورون بثقة عملائنا في أكثر من 500 مشروع منجز
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-accent text-accent-foreground">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => {
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <IconLoader name={stat.iconName} className="h-12 w-12 text-primary mx-auto mb-4" />
                    <div className="text-4xl font-bold text-primary mb-2">{stat.number}</div>
                    <div className="text-sm">{stat.label}</div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-8 hover-lift h-full">
                    <div className="flex items-start justify-between mb-4">
                      <Badge className="bg-gradient-primary text-accent">{project.type}</Badge>
                      <span className="text-sm text-muted-foreground">{project.date}</span>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-3">{project.name}</h3>
                    
                    <div className="flex items-center gap-2 text-muted-foreground mb-4">
                      <IconLoader name="MapPin" className="h-4 w-4" />
                      <span className="text-sm">{project.location}</span>
                    </div>

                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      {project.description}
                    </p>

                    <div className="border-t border-border pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">النوع:</span>
                        <span className="font-medium">{project.category}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">عدد الأبواب:</span>
                        <span className="font-bold text-primary">{project.doors} باب</span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-24 bg-card">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">
                ماذا يقول <span className="text-gradient">عملاؤنا</span>؟
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[
                {
                  name: "م. أحمد السالم",
                  role: "مقاول معتمد",
                  text: "بعد تجربة جميع أنواع الأبواب في مشاريعنا، أثبتت أبواب WPC من New Business أنها الخيار الأمثل من حيث الجودة والسعر. لا شكاوى من العملاء، ولا مشاكل بعد التركيب.",
                },
                {
                  name: "خالد العتيبي",
                  role: "مالك فيلا",
                  text: "اخترت أبواب WPC لفيلتي وأنا سعيد جداً بالنتيجة. الأبواب أنيقة، متينة، ولم تحتج لأي صيانة منذ أكثر من سنتين. أنصح بها بشدة.",
                },
                {
                  name: "سارة محمد",
                  role: "مصممة داخلية",
                  text: "أعتمد على New Business في جميع مشاريعي. تنوع التصاميم والألوان يمنحني حرية كبيرة في الإبداع، والجودة تضمن رضا عملائي.",
                },
              ].map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-8 hover-lift h-full">
                    <p className="text-muted-foreground leading-relaxed mb-6 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="border-t border-border pt-4">
                      <div className="font-bold text-lg">{testimonial.name}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Projects;
